// =============================================================================
// Bootstrap slice — Phase 2 lifecycle gate.
//
// FSM:  inactive  → (preloadedState seeds 'hydrating' iff bootstrap thunk
//                    will run)  →  ready  (terminal until reset)
//
//       ready  →  (BOOTSTRAP_RESET)  →  inactive
//                    Phase 4+ soft logout/login may cascade BOOTSTRAP_RESET
//                    so the next bootstrapApp() invocation re-runs the
//                    profile-fetch lifecycle for the new session.
//
// 'inactive' = scaffolding-only state. Reducer default; also the post-RESET
// state. Honest "no lifecycle configured here". Should not be observed at
// runtime once preloadedState in store.js seeds 'hydrating'.
//
// 'hydrating' = bootstrap thunk is in flight. App.js renders Splash while in
// this phase. Set ONLY via preloadedState in store.js (no action transitions
// into 'hydrating' — it is the entry condition for the lifecycle, not a
// reachable state from elsewhere).
//
// 'ready' = bootstrap attempt complete (success OR failure OR no-auth).
// App.js mounts routes. BOOTSTRAP_READY is idempotent.
//
// -----------------------------------------------------------------------------
// INVARIANT (load-bearing for Phase 4+ dual-write reasoning):
//
//   bootstrap.phase === 'ready' means:
//       "the initial hydration attempt has completed"
//
//   bootstrap.phase === 'ready' DOES NOT mean:
//       "all slices are mutually synchronized"
//       "auth, profile, and userLogin agree on the same identity/data"
//       "profile.data is non-null"
//       "auth.access is fresh / not expired"
//       "any subsequent mutation has propagated to every replica"
//
// Phase 4A introduces dual-write (legacy + auth in the same tick), but the
// dual-write contract is per-action, not a slice-wide invariant. Profile
// dual-write arrives in Phase 4B. Until Phase 6 ownership inversion, treat
// bootstrap.ready as a UI gate only — it tells you the Splash can come down,
// nothing more about cross-slice coherence.
// -----------------------------------------------------------------------------
// =============================================================================

import {
  BOOTSTRAP_READY,
  BOOTSTRAP_RESET,
} from '../constants/bootstrapConstants';

const INITIAL_BOOTSTRAP_STATE = {
  phase: 'inactive',
};

export const bootstrapReducers = (state = INITIAL_BOOTSTRAP_STATE, action) => {
  switch (action.type) {
    case BOOTSTRAP_READY:
      // Idempotent: tolerate double-dispatch under React StrictMode mount,
      // double bootstrapApp() invocation, etc.
      if (state.phase === 'ready') return state;
      return { ...state, phase: 'ready' };

    case BOOTSTRAP_RESET:
      // Idempotent reset to scaffolding state. Returning the INITIAL ref is
      // intentional — repeat resets do not churn subscribers. Constraint:
      // caller must pair this with a fresh bootstrapApp() dispatch when the
      // intent is to re-hydrate (e.g. soft account switch in Phase 4+).
      return INITIAL_BOOTSTRAP_STATE;

    default:
      return state;
  }
};
